import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  WeekDriverDetails,
  DriverUnavailableDetail,
} from '../../../interfaces/holidays.interface';

interface DialogData {
  weekData?: WeekDriverDetails;
  weekNumber: number;
  type: 'vacation' | 'unavailable';
  doble_tripulacio?: number;
}

@Component({
  selector: 'app-drivers-not-available-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './drivers-not-available-dialog.component.html',
  styleUrls: ['./drivers-not-available-dialog.component.css', '../../../styles/layout.css'],
})
export class DriversNotAvailableDialogComponent {
  weekData?: WeekDriverDetails;
  weekNumber: number;
  type: 'vacation' | 'unavailable';
  doble_tripulacio: number = 0;
  isLoading: boolean = true;

  // Filtered lists
  vacationDrivers: DriverUnavailableDetail[] = [];
  unavailableDrivers: DriverUnavailableDetail[] = [];

  constructor(
    public dialogRef: MatDialogRef<DriversNotAvailableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.weekNumber = data.weekNumber;
    this.type = data.type;
    this.doble_tripulacio = data.doble_tripulacio ?? 0;
    if (data.weekData) {
      this.setWeekData(data.weekData);
    }
  }

  setWeekData(weekData: WeekDriverDetails): void {
    this.weekData = weekData;

    // Filter drivers based on _type field
    // "v" = vacation, anything else = unavailable (medical leave, etc.)
    const allDrivers = [
      ...(weekData.driversOnHoliday || []),
      ...(weekData.driversUnavailable || [])
    ];

    this.vacationDrivers = allDrivers.filter(driver => driver._type === 'v');
    this.unavailableDrivers = allDrivers.filter(driver => driver._type !== 'v');

    this.isLoading = false;
  }

  close(): void {
    this.dialogRef.close();
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  getTypeLabel(type: string): string {
    const typeMap: { [key: string]: string } = {
      'ac': 'AUSENCIA COMPENSAR',
      'ar': 'AUSENCIA RETRIBUIDA',
      'ba': 'BAJA ACCIDENTE',
      'bm': 'BAJA MALALTIA',
      'bmp': 'BAJA MATERNIDAD/PATERNIDAD',
      'e': 'EXCEDENCIA',
      'f': 'FESTIVO',
      'fr': 'FESTIVO',
      'p': 'PERSONAL',
      'pm': 'PERSONAL',
      'v': 'VACACIONES',
      'vp': 'VACACIONES ANTERIORES',
    };

    return typeMap[type?.toLowerCase()] || 'OTROS';
  }

  getTypeBadgeClass(type: string): string {
    const normalizedType = type?.toLowerCase();

    // Vacation types - green
    if (['v', 'vp'].includes(normalizedType)) {
      return 'badge-vacation';
    }

    // Medical leave types - red
    if (['ba', 'bm', 'bmp'].includes(normalizedType)) {
      return 'badge-medical';
    }

    // Personal/compensatory leave - blue
    if (['ac', 'ar', 'p', 'pm'].includes(normalizedType)) {
      return 'badge-personal';
    }

    // Holiday - purple
    if (['f', 'fr'].includes(normalizedType)) {
      return 'badge-holiday';
    }

    // Excedencia - orange
    if (normalizedType === 'e') {
      return 'badge-excedencia';
    }

    // Others - gray
    return 'badge-other';
  }
}
